import { useEffect, useId, useMemo, useState, type CSSProperties } from 'react'
import { GlassPanel } from '@/components/sections/GlassPanel'
import { Icon, Mark } from '@/components/brand/Brand'
import { useIsClient } from '@/components/motion/motion'

/**
 * The unit economics playground (/unit-economics-calculator), unlisted and shared by link.
 * Every assumption is visible in the controls or model constants, and the inputs round-trip
 * through the query string so a firm's numbers can be shared as a link.
 */

const BASE_FEE = 405
const BASE_CLIENTS = 140
const BASE_ADDED_CLIENTS = 500
const BASE_WALLET_GROWTH = 10
const CPA_CLIENTS_TODAY = 140
const CPA_CLIENTS_UNITED = 500
const CPA_SALARY = 125_000

const MODELS = {
  regular: {
    name: 'Regular CPA firm',
    short: 'Regular firm',
    production: 185,
    cpa: 125,
    admin: 35,
    software: 15,
    clientsPerProductionHire: 27,
    productionSalary: 60_000,
    productionNoun: 'bookkeepers',
  },
  offshore: {
    name: 'CPA firm with offshore staff',
    short: 'Offshore staff',
    production: 93,
    cpa: 125,
    admin: 28,
    software: 12,
    clientsPerProductionHire: 20,
    productionSalary: 22_000,
    productionNoun: 'offshore staff',
  },
  united: {
    name: 'United',
    short: 'United',
    production: 0,
    cpa: 35,
    admin: 10,
    software: 30,
    clientsPerProductionHire: Number.POSITIVE_INFINITY,
    productionSalary: 0,
    productionNoun: 'production hires',
  },
} as const

const MODEL_KEYS = ['regular', 'offshore', 'united'] as const
const COMPARATOR_KEYS = ['regular', 'offshore'] as const
type ModelKey = (typeof MODEL_KEYS)[number]
type ComparatorKey = (typeof COMPARATOR_KEYS)[number]

const money = (value: number) => `$${Math.round(value).toLocaleString('en-US')}`
const parens = (value: number) => `(${Math.round(Math.abs(value)).toLocaleString('en-US')})`
const signed = (value: number) => (value < 0 ? parens(value) : money(value))
const percent = (value: number) => `${Math.round(value * 100)}%`

const compactMoney = (value: number) => {
  const magnitude = Math.abs(value)
  const text = magnitude >= 1_000_000
    ? `$${(magnitude / 1_000_000).toFixed(2).replace(/0$/, '').replace(/\.0$/, '')}M`
    : `$${Math.round(magnitude / 1_000)}k`

  return value < 0 ? `(${text.slice(1)})` : text
}

const payback = (margin: number) => {
  if (margin <= 0) return '—'
  const years = 1 / margin
  if (years >= 6) return `~${Math.round(years)} yrs`
  const months = years * 12
  return months <= 21 ? `${Math.round(months)} mo` : `${years.toFixed(1)} yrs`
}

const urlParams = (fromUrl: boolean) => new URLSearchParams(fromUrl ? window.location.search : '')

const initialStepValue = (raw: string | null, fallback: number, min: number, max: number, step: number) => {
  const parsed = Number(raw)
  if (raw === null || raw === '' || !Number.isFinite(parsed)) return fallback
  return Math.min(max, Math.max(min, Math.round(parsed / step) * step))
}

const inputNumber = (value: string, fallback: number) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback
}

/**
 * The prerendered HTML shows the default model. A shared link can carry its numbers in the query
 * string, so once the page is interactive the model starts again from those (a second mount, keyed
 * on the switch), and the server and browser renders still match while hydrating.
 */
export const UnitEconomicsCalculator = () => {
  const isClient = useIsClient()
  return <CalculatorModel key={isClient ? 'url' : 'defaults'} seedFromUrl={isClient} />
}

const CalculatorModel = ({ seedFromUrl }: { seedFromUrl: boolean }) => {
  const initialParams = () => urlParams(seedFromUrl)
  const feeId = useId()
  const clientsId = useId()
  const addedClientsId = useId()
  const walletGrowthId = useId()

  const [feeInput, setFeeInput] = useState(() => initialParams().get('fee') ?? String(BASE_FEE))
  const [clientsInput, setClientsInput] = useState(() => initialParams().get('clients') ?? String(BASE_CLIENTS))
  const [comparator, setComparator] = useState<ComparatorKey>(() => {
    const raw = initialParams().get('vs') ?? ''
    return (COMPARATOR_KEYS as readonly string[]).includes(raw) ? (raw as ComparatorKey) : 'regular'
  })
  const [addedClients, setAddedClients] = useState(() =>
    initialStepValue(initialParams().get('add'), BASE_ADDED_CLIENTS, 100, 1000, 100),
  )
  const [walletGrowth, setWalletGrowth] = useState(() =>
    initialStepValue(initialParams().get('wallet'), BASE_WALLET_GROWTH, 0, 40, 1),
  )

  useEffect(() => {
    // The first, default render must not overwrite numbers a shared link arrived with.
    if (!seedFromUrl) return
    const params = new URLSearchParams()
    if (feeInput !== String(BASE_FEE)) params.set('fee', feeInput)
    if (clientsInput !== String(BASE_CLIENTS)) params.set('clients', clientsInput)
    if (comparator !== 'regular') params.set('vs', comparator)
    if (addedClients !== BASE_ADDED_CLIENTS) params.set('add', String(addedClients))
    if (walletGrowth !== BASE_WALLET_GROWTH) params.set('wallet', String(walletGrowth))

    const query = params.toString()
    window.history.replaceState(null, '', query ? `${window.location.pathname}?${query}` : window.location.pathname)
  }, [addedClients, clientsInput, comparator, feeInput, seedFromUrl, walletGrowth])

  const fee = inputNumber(feeInput, BASE_FEE)
  const clients = inputNumber(clientsInput, BASE_CLIENTS)

  const calculations = useMemo(() => {
    const economics = (key: ModelKey) => {
      const model = MODELS[key]
      const cost = model.production + model.cpa + model.admin + model.software
      const profit = fee - cost
      return { ...model, key, cost, profit, margin: profit / fee }
    }

    const all = {
      regular: economics('regular'),
      offshore: economics('offshore'),
      united: economics('united'),
    }

    const growth = {
      regular: {
        productionHires: Math.ceil(addedClients / MODELS.regular.clientsPerProductionHire),
        cpaHires: Math.ceil(addedClients / CPA_CLIENTS_TODAY),
      },
      offshore: {
        productionHires: Math.ceil(addedClients / MODELS.offshore.clientsPerProductionHire),
        cpaHires: Math.ceil(addedClients / CPA_CLIENTS_TODAY),
      },
      united: {
        productionHires: 0,
        cpaHires: Math.ceil(addedClients / CPA_CLIENTS_UNITED),
      },
    }

    const growthWithEconomics = Object.fromEntries(
      MODEL_KEYS.map((key) => {
        const item = growth[key]
        return [
          key,
          {
            ...item,
            payroll:
              item.productionHires * MODELS[key].productionSalary + item.cpaHires * CPA_SALARY,
            grossProfit: addedClients * 12 * all[key].profit,
          },
        ]
      }),
    ) as Record<ModelKey, { productionHires: number; cpaHires: number; payroll: number; grossProfit: number }>

    const rates = {
      regular: Math.max(all.regular.margin, 0),
      offshore: Math.max(all.offshore.margin, 0),
      united: (1 + Math.max(all.united.margin, 0)) * (1 + walletGrowth / 100) - 1,
    }
    const years = [0, 1, 2, 3, 4, 5]
    const runway = {
      regular: years.map((year) => (1 + rates.regular) ** year),
      offshore: years.map((year) => (1 + rates.offshore) ** year),
      united: years.map((year) => (1 + rates.united) ** year),
    }

    return { all, growth: growthWithEconomics, runway }
  }, [addedClients, fee, walletGrowth])

  const { all, growth, runway } = calculations
  const selected = all[comparator]
  const grossProfitLift = all.united.profit - selected.profit
  const maxRunway = Math.max(...runway.united)
  const annualRevenue = fee * clients * 12

  return (
    <div className="calc" id="unit-economics-top">
      {/* The glass stays still until the pointer stirs it: this page has no pause control. */}
      <GlassPanel className="hero calc-hero" shade="hero" labelledBy="unit-economics-title" ambient={false}>
        <div className="nav-space" aria-hidden="true" />
        <div className="calc-hero-body">
          <div className="calc-subnav" aria-label="Calculator shortcuts">
            <p>The unit economics playground</p>
            <a href="#unit-economics-flywheel">Jump to the flywheel <span aria-hidden>↓</span></a>
          </div>
          <div className="calc-hero-copy">
            <p className="calc-eyebrow"><span>Live model</span> One client at a time</p>
            <h1 id="unit-economics-title">What if adding a client didn&apos;t mean adding a <em>person?</em></h1>
            <p className="calc-hero-lede">
              One client-month. Three operating models. Move the levers and watch the economics turn into a growth
              machine.
            </p>
          </div>

          <div className="calc-control-deck" aria-label="Calculator controls">
            <div className="calc-input-pair">
              <label htmlFor={feeId}>
                <span>Monthly fee</span>
                <span className="calc-money-input">
                  <b aria-hidden>$</b>
                  <input
                    id={feeId}
                    type="number"
                    inputMode="decimal"
                    min={100}
                    step={5}
                    value={feeInput}
                    onChange={(event) => setFeeInput(event.target.value)}
                    onBlur={() => setFeeInput(String(Math.max(100, inputNumber(feeInput, BASE_FEE))))}
                    aria-label="Average monthly fee ($/client)"
                  />
                </span>
              </label>
              <span className="calc-input-operator" aria-hidden>×</span>
              <label htmlFor={clientsId}>
                <span>Monthly clients</span>
                <input
                  id={clientsId}
                  type="number"
                  inputMode="numeric"
                  min={10}
                  step={5}
                  value={clientsInput}
                  onChange={(event) => setClientsInput(event.target.value)}
                  onBlur={() => setClientsInput(String(Math.max(10, inputNumber(clientsInput, BASE_CLIENTS))))}
                  aria-label="Monthly accounting clients"
                />
              </label>
            </div>
            <div className="calc-run-rate">
              <span>Annual accounting revenue</span>
              <strong>{compactMoney(annualRevenue)}</strong>
            </div>
          </div>

          <div className="calc-versus-stage">
            <div className="calc-model-showdown">
              <div className="calc-compare-switch" role="group" aria-label="Choose comparison model">
                {COMPARATOR_KEYS.map((key) => (
                  <button
                    key={key}
                    type="button"
                    className={comparator === key ? 'is-active' : undefined}
                    onClick={() => setComparator(key)}
                    aria-label={MODELS[key].name}
                    aria-pressed={comparator === key}
                  >
                    {key === 'regular' ? 'Regular CPA' : 'Offshore staff'}
                  </button>
                ))}
              </div>

              <div className="calc-duel-cards">
                <article className="calc-duel-card">
                  <p>{selected.name}</p>
                  <div
                    className="calc-margin-orbit"
                    style={{ '--fill': `${Math.max(0, selected.margin) * 360}deg` } as CSSProperties}
                    aria-label={`${selected.name} gross margin ${percent(selected.margin)}`}
                  >
                    <div><strong>{percent(selected.margin)}</strong><span>margin</span></div>
                  </div>
                  <footer><span>keeps</span><b>{signed(selected.profit)}</b></footer>
                </article>

                <div className="calc-versus-badge" aria-hidden>VS</div>

                <article className="calc-duel-card calc-duel-card--united">
                  <div className="calc-best-tag">WITH JUSTINE</div>
                  <p>United</p>
                  <div
                    className="calc-margin-orbit"
                    style={{ '--fill': `${Math.max(0, all.united.margin) * 360}deg` } as CSSProperties}
                    aria-label={`United gross margin ${percent(all.united.margin)}`}
                  >
                    <div><strong>{percent(all.united.margin)}</strong><span>margin</span></div>
                  </div>
                  <footer><span>keeps</span><b>{signed(all.united.profit)}</b></footer>
                </article>
              </div>

              <div className="calc-profit-pop">
                <span aria-hidden>✦</span>
                <strong>+{money(grossProfitLift)}</strong>
                <span>more gross profit, per client-month</span>
              </div>
            </div>
          </div>
        </div>
      </GlassPanel>

      <section className="calc-cost-section calc-shell" id="unit-economics-one-client">
        <div className="calc-section-heading">
          <p className="calc-chapter">01 / The monthly squeeze</p>
          <h2>Follow every dollar.</h2>
          <p>Same client. Same fee. Very different amount left in the tank.</p>
        </div>

        <div className="calc-cost-board">
          {MODEL_KEYS.map((key, index) => {
            const model = all[key]
            const costs = [
              { label: 'Production — humans', value: model.production, note: key === 'united' ? 'zero people' : '' },
              { label: 'CPA judgment', value: model.cpa, note: key === 'united' ? 'same CPAs · ~4× the clients' : '' },
              { label: 'Admin', value: model.admin, note: '' },
              { label: 'Software', value: model.software, note: key === 'united' ? 'incl. agents & compute' : '' },
            ]

            return (
              <article className={`calc-cost-row${key === 'united' ? ' is-featured' : ''}`} key={key}>
                <header>
                  <span className="calc-model-number">{String.fromCharCode(65 + index)}</span>
                  <div><strong>{model.name}</strong><small>{money(fee)} fee</small></div>
                </header>
                <div className="calc-cost-meter" aria-label={`${model.name} allocation of monthly fee`}>
                  {costs.filter((cost) => cost.value > 0).map((cost) => (
                    <div
                      key={cost.label}
                      className="calc-cost-slice"
                      style={{ '--slice-width': `${Math.max(0, (cost.value / fee) * 100)}%` } as CSSProperties}
                      title={`${cost.label}: ${money(cost.value)}`}
                    />
                  ))}
                  <div
                    className="calc-profit-slice"
                    style={{ '--slice-width': `${Math.max(0, model.margin * 100)}%` } as CSSProperties}
                    title={`Gross profit: ${signed(model.profit)}`}
                  />
                </div>
                <div className="calc-cost-details">
                  {costs.map((cost) => (
                    <div key={cost.label}>
                      <span>{cost.label}</span>
                      <b>{key === 'united' && cost.value === 0 ? '$0' : parens(cost.value)}</b>
                      {cost.note ? <small>{cost.note}</small> : null}
                    </div>
                  ))}
                  <div className="calc-gross-detail">
                    <span>Gross profit</span>
                    <b>{percent(model.margin)}</b>
                    <small>{signed(model.profit)}</small>
                  </div>
                </div>
              </article>
            )
          })}
        </div>

        <div className="calc-meter-legend" aria-label="Cost bar legend">
          <span><i className="calc-cost-key" /> operating cost</span>
          <span><i className="calc-profit-key" /> gross profit</span>
        </div>
      </section>

      <section className="calc-growth-section" id="unit-economics-growth">
        <div className="calc-shell calc-growth-inner">
          <div className="calc-section-heading calc-split-heading">
            <div>
              <p className="calc-chapter">02 / The client drop</p>
              <h2>Add clients.<br />Skip the hiring spree.</h2>
            </div>
            <p>Drop a batch of clients into each model and see how many people—and how much payroll—it takes to catch them.</p>
          </div>

          <div className="calc-drop-console">
            <div className="calc-drop-value">
              <span>Drop in</span>
              <strong>+{addedClients.toLocaleString('en-US')}</strong>
              <em>new clients</em>
            </div>
            <label className="calc-range-wrap" htmlFor={addedClientsId}>
              <span className="sr-only">Number of new clients</span>
              <input
                id={addedClientsId}
                type="range"
                min={100}
                max={1000}
                step={100}
                value={addedClients}
                onChange={(event) => setAddedClients(Number(event.target.value))}
                aria-label="Add clients…"
              />
              <span className="calc-range-labels"><b>100</b><b>500</b><b>1,000</b></span>
            </label>
            <span className="calc-drag-hint">Drag the client drop →</span>
          </div>

          <div className="calc-hiring-lanes">
            {MODEL_KEYS.map((key) => {
              const item = growth[key]
              const model = MODELS[key]
              const totalHires = item.productionHires + item.cpaHires

              return (
                <article className={`calc-hiring-lane${key === 'united' ? ' is-featured' : ''}`} key={key}>
                  <div className="calc-lane-head">
                    <span>{model.name}</span>
                    <strong>{totalHires} new {totalHires === 1 ? 'hire' : 'hires'}</strong>
                  </div>
                  <div className="calc-people-track" aria-hidden>
                    {Array.from({ length: Math.min(totalHires, 28) }).map((_, index) => (
                      <span className={index >= item.productionHires ? 'is-cpa' : undefined} key={index}>
                        <Icon name="person" />
                      </span>
                    ))}
                    {totalHires === 0 ? <b>No production hiring</b> : null}
                    {totalHires > 28 ? <em>+{totalHires - 28}</em> : null}
                  </div>
                  <dl>
                    <div>
                      <dt>Production hires</dt>
                      <dd>{key === 'united' ? '0' : `+${item.productionHires} ${model.productionNoun}`}</dd>
                    </div>
                    <div><dt>CPA hires</dt><dd>+{item.cpaHires}</dd></div>
                    <div><dt>New payroll / year</dt><dd>{compactMoney(item.payroll)}</dd></div>
                    <div className="calc-lane-profit">
                      <dt>Gross profit from new clients</dt><dd>{compactMoney(item.grossProfit)}</dd>
                    </div>
                  </dl>
                </article>
              )
            })}
          </div>
        </div>
      </section>

      <section className="calc-flywheel-section calc-shell" id="unit-economics-flywheel">
        <div className="calc-section-heading calc-flywheel-heading">
          <p className="calc-chapter">03 / The $1M flywheel</p>
          <h2>Buy a book at 1× revenue.<br /><em>Then let it feed itself.</em></h2>
          <p>All three reinvest the cash into more books at roughly 1×. United compounds on gross margin plus share-of-wallet growth.</p>
        </div>

        <div className="calc-wallet-control">
          <div>
            <span>United share-of-wallet growth</span>
            <strong>{walletGrowth}%<small> / year</small></strong>
          </div>
          <label htmlFor={walletGrowthId}>
            <span className="sr-only">United annual share of wallet growth percentage</span>
            <input
              id={walletGrowthId}
              type="range"
              min={0}
              max={40}
              step={1}
              value={walletGrowth}
              onChange={(event) => setWalletGrowth(Number(event.target.value))}
            />
            <span className="calc-range-labels"><b>0%</b><b>20%</b><b>40%</b></span>
          </label>
        </div>

        <div className="calc-runway-card">
          <div className="calc-runway-grid">
            <div className="calc-year-axis" aria-hidden>
              {[0, 1, 2, 3, 4, 5].map((year) => <span key={year}>Yr {year}</span>)}
            </div>
            {MODEL_KEYS.map((key) => {
              const model = all[key]
              const values = runway[key]

              return (
                <div className={`calc-runway-row${key === 'united' ? ' is-featured' : ''}`} key={key}>
                  <div className="calc-runway-name">
                    <i className={`calc-model-key calc-model-key--${key}`} />
                    <strong>{model.short}</strong>
                  </div>
                  <div className="calc-runway-bars" aria-label={`${model.name} revenue from year zero to year five`}>
                    {values.map((value, year) => (
                      <div className="calc-bar-slot" key={year}>
                        <span
                          className={`calc-runway-bar calc-runway-bar--${key}`}
                          style={{ '--bar-height': `${Math.max(5, (value / maxRunway) * 100)}%` } as CSSProperties}
                          title={`Year ${year}: $${value.toFixed(1)}M`}
                        />
                        <b>{year === 5 ? `$${value.toFixed(1)}M` : ''}</b>
                      </div>
                    ))}
                  </div>
                </div>
              )
            })}
          </div>
          <div className="calc-runway-note">Revenue after reinvesting cash each year</div>
        </div>

        <div className="calc-book-cards">
          {MODEL_KEYS.map((key) => {
            const model = all[key]
            const finalValue = runway[key][5] * 1_000_000

            return (
              <article className={key === 'united' ? 'is-featured' : undefined} key={key}>
                <header>
                  <span>{model.name}</span>
                  {key === 'united' ? <b>Gross margin + {walletGrowth}% wallet</b> : null}
                </header>
                <dl>
                  <div>
                    <dt>Gross margin</dt>
                    <dd>{key === 'united' ? `${percent(model.margin)} + ${walletGrowth}% wallet` : percent(model.margin)}</dd>
                  </div>
                  <div><dt>Cash in year 1</dt><dd>{compactMoney(model.margin * 1_000_000)}</dd></div>
                  <div><dt>Payback on the $1M</dt><dd>{payback(model.margin)}</dd></div>
                  <div className="calc-five-year">
                    <dt>Revenue owned after 5 yrs</dt><dd>{compactMoney(finalValue)}</dd>
                  </div>
                </dl>
              </article>
            )
          })}
        </div>
      </section>

      <aside className="calc-model-note calc-shell">
        <div className="calc-note-mark" aria-hidden>
          <Mark />
        </div>
        <div>
          <strong>No mystery math.</strong>
          <p>
            Assumptions live in the controls and cells above. Books are priced at ~1× annual fees. United software
            includes agents and compute. Figures are per client-month unless labeled.
          </p>
        </div>
        <a href="#unit-economics-top">Reset your view <span aria-hidden>↗</span></a>
      </aside>
    </div>
  )
}
